import { logActivity } from '../utils/activityLog';
import React, { useEffect, useState } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Wallet, TrendingDown, RefreshCw, TrendingUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { dashboardService } from '../services/dashboard';
import ResultPanel from '../components/analytics/ResultPanel';
import { getCurrentUser, getResultAccess } from '../utils/roles';

/* ── Stat Card ──────────────────────────────────────── */
const StatCard = ({ label, value, sub, icon: Icon, colorFrom, colorTo, glowColor, isDark }) => (
  <div
    className="relative rounded-2xl overflow-hidden p-6"
    style={{
      background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
      border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(99,102,241,0.12)',
      boxShadow: isDark
        ? `0 0 0 1px rgba(255,255,255,0.03), 0 8px 32px rgba(0,0,0,0.4)`
        : '0 4px 24px rgba(99,102,241,0.07)',
    }}
  >
    {/* Top gradient accent line */}
    <div
      className="absolute top-0 left-0 right-0 h-px"
      style={{ background: `linear-gradient(90deg, transparent, ${colorFrom}, ${colorTo}, transparent)` }}
    />

    {/* Ambient glow blob */}
    <div
      className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
      style={{
        background: `radial-gradient(circle at top right, ${glowColor} 0%, transparent 70%)`,
        borderRadius: '50%',
        filter: 'blur(8px)',
      }}
    />

    <div className="flex items-start gap-4 relative">
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{
          background: `linear-gradient(135deg, ${colorFrom}22, ${colorTo}11)`,
          border: `1px solid ${colorFrom}30`,
        }}
      >
        <Icon size={20} style={{ color: colorFrom }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8' }}>
          {label}
        </p>
        <p
          className="text-2xl font-bold mt-1 tracking-tight"
          style={{
            background: `linear-gradient(135deg, ${colorFrom}, ${colorTo})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {value}
        </p>
        <p className="text-xs mt-1" style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#94a3b8' }}>{sub}</p>
      </div>
    </div>
  </div>
);

/* ── Data Card ──────────────────────────────────────── */
const DataCard = ({ title, onRefresh, isDark, children }) => (
  <div
    className="rounded-2xl p-6"
    style={{
      background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
      border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(99,102,241,0.12)',
      boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.3)' : '0 4px 24px rgba(99,102,241,0.06)',
    }}
  >
    <div className="flex items-center justify-between mb-5">
      <h3 className="text-sm font-semibold tracking-tight" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
        {title}
      </h3>
      {onRefresh && (
        <button
          onClick={onRefresh}
          className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200"
          style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8', background: 'transparent' }}
          onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(99,102,241,0.07)'; e.currentTarget.style.color = isDark ? '#f1f5f9' : '#4f46e5'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8'; }}
        >
          <RefreshCw size={14} />
        </button>
      )}
    </div>
    {children}
  </div>
);

/* ── Table ──────────────────────────────────────────── */
const StyledTable = ({ headers, rows, isDark }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th
              key={i}
              className="text-left pb-3 pt-1 font-semibold uppercase tracking-wider"
              style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8', fontSize: '10px' }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={i}
            className="transition-colors"
            style={{
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(99,102,241,0.06)'}`,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(99,102,241,0.03)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            {row.map((cell, j) => (
              <td key={j} className="py-2.5 pr-3 font-medium" style={{ color: isDark ? '#cbd5e1' : '#334155' }}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ── Custom Tooltip ─────────────────────────────────── */
const CustomTooltip = ({ active, payload, label, isDark }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      className="rounded-xl px-3 py-2.5 text-xs"
      style={{
        background: isDark ? 'rgba(8,12,24,0.95)' : '#fff',
        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(99,102,241,0.15)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <p className="font-semibold mb-1" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name}: <span className="tabular-nums">${p.value?.toFixed(0)}</span>
        </p>
      ))}
    </div>
  );
};

const fmtDashboardMoney = (value) => `$${(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDashboardNum = (value) => (value ?? 0).toLocaleString('en-US');
const translatePeriod = (value, t) => {
  const periodKey = {
    Today: 'today',
    Yesterday: 'yesterday',
    'This Month': 'thisMonthRow',
    'Last Month': 'lastMonth',
  }[value];

  return periodKey ? t(`dashboard.${periodKey}`) : value;
};

const SnapshotMetric = ({ label, value, sub, tone, isDark }) => {
  const tones = {
    purple: '#8b5cf6',
    green: '#10b981',
    amber: '#f59e0b',
    red: '#f43f5e',
    blue: '#38bdf8',
  };
  const color = tones[tone] || tones.purple;
  return (
    <div
      className="rounded-xl px-4 py-3"
      style={{
        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(99,102,241,0.03)',
        border: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(99,102,241,0.08)',
      }}
    >
      <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: isDark ? 'rgba(255,255,255,0.28)' : '#94a3b8' }}>{label}</div>
      <div className="mt-1 text-lg font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="mt-0.5 text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.24)' : '#94a3b8' }}>{sub}</div>
    </div>
  );
};

const Dashboard = () => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const currentUser = getCurrentUser();
  const { canView: canViewResult, canSwitchDepartment: canSwitchResultDept, fixedDepartment: fixedResultDept } = getResultAccess(currentUser);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalDeposits: 0, totalWithdrawals: 0, net: 0 });
  const [productivityData, setProductivityData] = useState([]);
  const [trafficData, setTrafficData] = useState([]);
  const [turnoverData, setTurnoverData] = useState([]);
  const [txSummary, setTxSummary] = useState({ deposits: 0, withdrawals: 0, totalDepositsAmt: 0, totalWithdrawalsAmt: 0 });
  const [todaySnapshot, setTodaySnapshot] = useState({ ftdNum: 0, ftdAmount: 0, reDepNum: 0, reDepAmount: 0, deposits: 0, depositAmount: 0, withdrawals: 0, withdrawalAmount: 0, net: 0 });
  const [mainTab, setMainTab] = useState('dashboard');
  const [resultDept, setResultDept] = useState(() => fixedResultDept || 'sales');
  const [resultData, setResultData] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);

  useEffect(() => {
    logActivity('view', 'dashboard', null, 'Viewed Dashboard');
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (!canViewResult) return;
    loadResultData(resultDept);
  }, [resultDept, canViewResult]);

  useEffect(() => {
    if (!canViewResult && mainTab === 'result') setMainTab('dashboard');
  }, [canViewResult, mainTab]);

  useEffect(() => {
    if (fixedResultDept && resultDept !== fixedResultDept) setResultDept(fixedResultDept);
  }, [fixedResultDept, resultDept]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, productivity, traffic, turnover, snapshot] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getProductivity(),
        dashboardService.getTraffic(),
        dashboardService.getTurnover(),
        dashboardService.getTodaySnapshot(),
      ]);
      setStats(statsData);
      setProductivityData(productivity);
      setTrafficData(traffic);
      setTurnoverData(turnover);
      setTodaySnapshot(snapshot);
      setTxSummary({
        deposits: statsData.totalDeposits > 0 ? Math.round(statsData.totalDeposits / 100) : 0,
        withdrawals: statsData.totalWithdrawals > 0 ? Math.round(statsData.totalWithdrawals / 100) : 0,
        totalDepositsAmt: statsData.totalDeposits,
        totalWithdrawalsAmt: statsData.totalWithdrawals,
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadResultData = async (department = resultDept) => {
    try {
      setResultLoading(true);
      const data = await dashboardService.getResults(department);
      setResultData(data);
    } catch (error) {
      console.error('Failed to load dashboard results:', error);
      setResultData(null);
    } finally {
      setResultLoading(false);
    }
  };

  const txPieData = [
    { name: t('dashboard.deposits'),    value: txSummary.totalDepositsAmt,    color: '#6366f1' },
    { name: t('dashboard.withdrawals'), value: txSummary.totalWithdrawalsAmt, color: '#f43f5e' },
  ];

  const gridColor   = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(99,102,241,0.08)';
  const axisColor   = isDark ? 'rgba(255,255,255,0.2)'  : '#94a3b8';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div
            className="w-10 h-10 rounded-full border-2 border-transparent"
            style={{
              borderTopColor: '#6366f1',
              borderRightColor: '#8b5cf6',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <p className="text-sm font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>
            Loading dashboard...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 max-w-[1600px]">
      <div
        className="inline-flex rounded-xl p-1"
        style={{
          background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
          border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(99,102,241,0.12)',
        }}
      >
        {[
          { key: 'dashboard', label: 'Dashboard' },
          ...(canViewResult ? [{ key: 'result', label: 'Result' }] : []),
        ].map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setMainTab(tab.key)}
            className="rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
            style={{
              background: mainTab === tab.key ? '#8b5cf6' : 'transparent',
              color: mainTab === tab.key ? '#fff' : (isDark ? 'rgba(255,255,255,0.55)' : '#64748b'),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {mainTab === 'dashboard' ? (
        <>
      {/* ── Stats ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label={t('dashboard.totalDeposits')}
          value={`$${stats.totalDeposits.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
          sub={t('dashboard.thisMonth')}
          icon={Wallet}
          colorFrom="#6366f1"
          colorTo="#818cf8"
          glowColor="rgba(99,102,241,0.12)"
          isDark={isDark}
        />
        <StatCard
          label={t('dashboard.totalWithdrawals')}
          value={`$${stats.totalWithdrawals.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
          sub={t('dashboard.thisMonth')}
          icon={TrendingDown}
          colorFrom="#f59e0b"
          colorTo="#fbbf24"
          glowColor="rgba(245,158,11,0.10)"
          isDark={isDark}
        />
        <StatCard
          label={t('dashboard.net')}
          value={`$${stats.net.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
          sub={t('dashboard.thisMonth')}
          icon={stats.net >= 0 ? TrendingUp : TrendingDown}
          colorFrom="#10b981"
          colorTo="#34d399"
          glowColor="rgba(16,185,129,0.10)"
          isDark={isDark}
        />
      </div>

      {/* ── Tables ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4">
        <DataCard title={t('dashboard.todaySnapshot')} onRefresh={loadDashboardData} isDark={isDark}>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <SnapshotMetric label="FTD" value={fmtDashboardMoney(todaySnapshot.ftdAmount)} sub={`${fmtDashboardNum(todaySnapshot.ftdNum)} ${t('dashboard.transactions')}`} tone="purple" isDark={isDark} />
            <SnapshotMetric label="ReDep" value={fmtDashboardMoney(todaySnapshot.reDepAmount)} sub={`${fmtDashboardNum(todaySnapshot.reDepNum)} ${t('dashboard.transactions')}`} tone="amber" isDark={isDark} />
            <SnapshotMetric label={t('dashboard.deposits')} value={fmtDashboardMoney(todaySnapshot.depositAmount)} sub={`${fmtDashboardNum(todaySnapshot.deposits)} ${t('dashboard.payments')}`} tone="green" isDark={isDark} />
            <SnapshotMetric label={t('dashboard.withdrawals')} value={fmtDashboardMoney(todaySnapshot.withdrawalAmount)} sub={`${fmtDashboardNum(todaySnapshot.withdrawals)} ${t('dashboard.payments')}`} tone="red" isDark={isDark} />
            <SnapshotMetric label={t('dashboard.net')} value={fmtDashboardMoney(todaySnapshot.net)} sub={t('dashboard.todayBalance')} tone={todaySnapshot.net >= 0 ? 'green' : 'red'} isDark={isDark} />
          </div>
        </DataCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <DataCard title={t('dashboard.productivityTracker')} onRefresh={loadDashboardData} isDark={isDark}>
          <StyledTable
            headers={[t('dashboard.timeframe'), 'FTD #', "FTD's $", 'ReDep.#', 'RaDep.$']}
            rows={productivityData.map(r => [
              translatePeriod(r.timeframe, t),
              r.ftdNum,
              r.ftdAmount.toFixed(2),
              r.reDepNum,
              r.reDepAmount.toLocaleString('en-US', { minimumFractionDigits: 2 }),
            ])}
            isDark={isDark}
          />
        </DataCard>

        <DataCard title={t('dashboard.trafficTracker')} onRefresh={loadDashboardData} isDark={isDark}>
          <StyledTable
            headers={[t('dashboard.timeframe'), t('dashboard.clientsCount'), 'FTD #', t('dashboard.conversionPercent')]}
            rows={trafficData.map(r => [translatePeriod(r.timeframe, t), r.clients, r.ftd, r.conv])}
            isDark={isDark}
          />
        </DataCard>
      </div>

      {/* ── Charts ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        {/* Line Chart */}
        <DataCard title={t('dashboard.turnoverTrend')} isDark={isDark}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={turnoverData}>
              <defs>
                <linearGradient id="depositGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
                <linearGradient id="withdrawalGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#f43f5e" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="day" stroke={axisColor} tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} />
              <YAxis stroke={axisColor} tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip isDark={isDark} />} />
              <Line type="monotone" dataKey="deposit"    stroke="url(#depositGrad)"    strokeWidth={2.5} dot={false} name="Deposit" />
              <Line type="monotone" dataKey="withdrawal" stroke="url(#withdrawalGrad)" strokeWidth={2.5} dot={false} name="Withdrawal" />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-6 mt-3">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', boxShadow: '0 0 6px rgba(99,102,241,0.6)' }} />
              <span className="text-xs font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b' }}>{t('dashboard.deposit')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'linear-gradient(135deg,#f59e0b,#f43f5e)', boxShadow: '0 0 6px rgba(245,158,11,0.5)' }} />
              <span className="text-xs font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b' }}>{t('dashboard.withdrawal')}</span>
            </div>
          </div>
        </DataCard>

        {/* Donut Chart */}
        <DataCard title={t('dashboard.transactionsThisMonth')} isDark={isDark}>
          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <defs>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                  </filter>
                </defs>
                <Pie
                  data={txPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={72}
                  paddingAngle={4}
                  dataKey="value"
                  strokeWidth={0}
                  filter="url(#glow)"
                >
                  {txPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle"
                  style={{ fontSize: 20, fontWeight: '800', fill: isDark ? '#f1f5f9' : '#0f172a', fontFamily: 'Inter, sans-serif' }}>
                  ${((txSummary.totalDepositsAmt + txSummary.totalWithdrawalsAmt) / 1000).toFixed(1)}k
                </text>
                <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle"
                  style={{ fontSize: 10, fontWeight: '600', fill: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8', fontFamily: 'Inter, sans-serif' }}>
                  {t('dashboard.netAmount')} ${(txSummary.totalDepositsAmt - txSummary.totalWithdrawalsAmt).toFixed(0)}
                </text>
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex items-center justify-center gap-6 mb-5">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#6366f1', boxShadow: '0 0 6px rgba(99,102,241,0.6)' }} />
              <span className="text-xs font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b' }}>
                {t('dashboard.deposits')}: ${txSummary.totalDepositsAmt.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#f43f5e', boxShadow: '0 0 6px rgba(244,63,94,0.5)' }} />
              <span className="text-xs font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b' }}>
                {t('dashboard.withdrawals')}: ${txSummary.totalWithdrawalsAmt.toFixed(2)}
              </span>
            </div>
          </div>

          {/* 7-Day Breakdown */}
          <div
            className="rounded-xl p-4"
            style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(99,102,241,0.03)', border: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(99,102,241,0.08)' }}
          >
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8', fontSize: '10px' }}>
              {t('dashboard.sevenDayBreakdown')}
            </p>
            <div className="space-y-1.5">
              <div className="grid grid-cols-[4rem_repeat(3,minmax(0,1fr))] gap-4 text-[10px] font-semibold uppercase tracking-wider">
                <span />
                <span className="text-left" style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8' }}>Deposit</span>
                <span className="text-left" style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8' }}>Withdraw</span>
                <span className="text-left" style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8' }}>Net</span>
              </div>
              {turnoverData.slice(-4).map((row, i) => (
                <div key={i} className="grid grid-cols-[4rem_repeat(3,minmax(0,1fr))] items-center gap-4 text-xs">
                  <span className="font-medium" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#94a3b8' }}>{row.day}</span>
                  <span className="text-left font-semibold tabular-nums" style={{ color: '#34d399' }}>+${row.deposit.toFixed(0)}</span>
                  <span className="text-left font-semibold tabular-nums" style={{ color: '#f87171' }}>-${row.withdrawal.toFixed(0)}</span>
                  <span className="text-left font-semibold tabular-nums" style={{ color: row.deposit - row.withdrawal >= 0 ? '#34d399' : '#f87171' }}>
                    ${(row.deposit - row.withdrawal).toFixed(0)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </DataCard>
      </div>

      {/* ── Footer ───────────────────────────────────────
      */}
        </>
      ) : (
        <ResultPanel
          data={resultData}
          department={resultDept}
          isDark={isDark}
          loading={resultLoading}
          canSwitchDepartment={canSwitchResultDept}
          onDepartmentChange={setResultDept}
        />
      )}

      <div className="text-center py-2">
        <p className="text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1' }}>
          {t('dashboard.copyright')}
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
